import { useEffect, useRef, useState } from 'react'
import useInfiniteScroll from './useInfiniteScroll'
import useOnScreen from './useOnScreen'

type InfiniteScrollWithTriggerOptions = {
  /** Unique identifier for pagination state in Redux store */
  paginationId: string
  /** Type of pagination (used in Redux-ORM selector) */
  paginationType: string
  /** Action creator that fetches a page of data */
  fetchAction: (params: { page: number; cancelToken?: any }) => any
  /** Callback fired when an error occurs during fetching */
  onError?: (error: any) => void
  /** Whether fetching is enabled */
  enabled?: boolean
  /** Intersection observer threshold (0.0 to 1.0) */
  threshold?: number
  /** Root margin for intersection observer */
  rootMargin?: string
}

type UseInfiniteScrollWithTriggerReturn = ReturnType<
  typeof useInfiniteScroll
> & {
  /** Ref to attach to the scroll trigger element */
  triggerRef: React.RefObject<HTMLDivElement>
}

/**
 * Hook that combines infinite scroll pagination with intersection observer trigger.
 *
 * Automatically loads more data when the trigger element enters the viewport.
 * Uses debouncing to prevent multiple simultaneous requests. Builds on top
 * of useInfiniteScroll with additional intersection observer functionality.
 *
 * @param options Configuration object including pagination and intersection observer settings
 * @returns Object containing pagination state, control functions, and trigger ref
 *
 * @example
 * ```tsx
 * const {
 *   recordIds,
 *   hasMore,
 *   isLoading,
 *   triggerRef
 * } = useInfiniteScrollWithTrigger({
 *   paginationId: 'products',
 *   paginationType: 'product',
 *   fetchAction: getProductsAction,
 *   threshold: 0.1,
 *   rootMargin: '50px'
 * })
 *
 * return (
 *   <div>
 *     {recordIds.map(id => <Item key={id} id={id} />)}
 *     {hasMore && <div ref={triggerRef}>Loading...</div>}
 *   </div>
 * )
 * ```
 */
export const useInfiniteScrollWithTrigger = ({
  paginationId,
  paginationType,
  fetchAction,
  onError,
  enabled = true,
  threshold = 0.1,
  rootMargin = '100px',
}: InfiniteScrollWithTriggerOptions): UseInfiniteScrollWithTriggerReturn => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [hasSeenIntersection, setHasSeenIntersection] = useState(false)
  const previousIntersectingRef = useRef(false)

  const infiniteScrollProps = useInfiniteScroll({
    paginationId,
    paginationType,
    fetchAction,
    onError,
    enabled,
  })

  const { hasMore, isLoading, loadMore } = infiniteScrollProps

  const isIntersecting = useOnScreen(triggerRef, { threshold, rootMargin })

  useEffect(() => {
    const wasIntersecting = previousIntersectingRef.current
    previousIntersectingRef.current = isIntersecting

    if (!wasIntersecting && isIntersecting) {
      setHasSeenIntersection(true)
    }

    if (
      isIntersecting &&
      hasSeenIntersection &&
      hasMore &&
      !isLoading &&
      enabled
    ) {
      loadMore()
      setHasSeenIntersection(false)
    }
  }, [
    isIntersecting,
    hasSeenIntersection,
    hasMore,
    isLoading,
    loadMore,
    enabled,
  ])

  return {
    ...infiniteScrollProps,
    triggerRef,
  }
}
